import type { Locale, TranslatedLocale } from '../locales';
import type { Translation } from '../translation';

// One chunk per language, so a player downloads only their own catalog. The Devvit inline budget
// lists `assets/catalog-*.js` as deferred; literal specifiers keep the chunks visible to its scanner.
const loaders: Readonly<Record<TranslatedLocale, () => Promise<Translation>>> = {
  ja: () => import('./catalog-ja').then((module) => module.ja),
  'zh-Hans': () => import('./catalog-zh-Hans').then((module) => module.zhHans),
  'zh-Hant': () => import('./catalog-zh-Hant').then((module) => module.zhHant),
  es: () => import('./catalog-es').then((module) => module.es),
  'pt-BR': () => import('./catalog-pt-BR').then((module) => module.ptBR),
  fr: () => import('./catalog-fr').then((module) => module.fr),
  de: () => import('./catalog-de').then((module) => module.de),
  id: () => import('./catalog-id').then((module) => module.id),
  vi: () => import('./catalog-vi').then((module) => module.vi),
  th: () => import('./catalog-th').then((module) => module.th),
};
const loaded: Partial<Record<TranslatedLocale, Translation>> = {};

/** Loads a translated catalog once. Korean and English are inline and need nothing. */
export async function loadTranslation(locale: TranslatedLocale): Promise<Translation> {
  const translation = loaded[locale] ?? await loaders[locale]();
  loaded[locale] = translation;
  return translation;
}

/**
 * Resolves true once `locale` can render translated (Korean and English at once), false if its
 * catalog fails. Unlike `prepareLocale` it has no deadline: screens a player stays on, such as
 * failure screens, can still repaint when a slow catalog finally lands.
 */
export function whenTranslationReady(locale: Locale): Promise<boolean> {
  if (locale === 'ko' || locale === 'en') {
    return Promise.resolve(true);
  }
  return loadTranslation(locale).then(
    () => true,
    () => false,
  );
}

/** Boot never waits longer than this for a catalog; a stalled request must not hang the shell. */
const catalogWaitMs = 4000;

/**
 * The language every surface should render: the requested one once its catalog is loaded, or
 * English when the catalog chunk fails or stalls (offline, captive portal, stale deployment).
 */
export async function prepareLocale(locale: Locale): Promise<Locale> {
  if (locale === 'ko' || locale === 'en') {
    return locale;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const ready = await Promise.race([
    loadTranslation(locale).then(
      () => true,
      (error: unknown) => {
        console.warn('[i18n] Falling back to English; the language catalog did not load.', error);
        return false;
      },
    ),
    new Promise<false>((resolve) => {
      timer = setTimeout(() => resolve(false), catalogWaitMs);
    }),
  ]);
  clearTimeout(timer);
  return ready ? locale : 'en';
}

/** Inline Korean/English copy, or the matching section of a loaded catalog (English until then). */
export function localized<T>(locale: Locale, ko: T, en: T, pick: (translation: Translation) => T): T {
  if (locale === 'ko') {
    return ko;
  }
  const translation = locale === 'en' ? undefined : loaded[locale];
  return translation ? pick(translation) : en;
}
